import type { ReactNode } from 'react';
import { Area, AreaChart, XAxis } from 'recharts';

import { getLocale } from '../../../i18n';
import { count } from '../../../chrome/measure/format';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../ui/chart';
import type { Span } from './figures';
import { NoReading } from './MetricsGrid';
import { ov } from './messages';
import { OverviewCard } from './OverviewCard';
import type { Overview } from '../api/schemas';

const hourLabel = (at: Date): string => `${String(at.getHours()).padStart(2, '0')}h`;

const dayLabel = (at: Date): string =>
  new Intl.DateTimeFormat(getLocale(), { day: '2-digit', month: '2-digit' }).format(at);

/** Hours between labelled ticks. Past a day the hours would repeat, so the
 *  axis labels each day, at its first hour. */
const TICK_EVERY_HOURS: Record<Span, number> = { '6h': 1, '1d': 3, '3d': 24, '7d': 24 };

export function MovementCard(props: { movement: Overview['movement']; span: Span }): ReactNode {
  const locale = getLocale();
  const measured = props.movement.some((bucket) => bucket.value !== null);
  const data = props.movement.map((bucket) => ({
    at: Date.parse(bucket.bucket),
    value: bucket.value,
  }));
  const ticks = data
    .filter((point) => new Date(point.at).getHours() % TICK_EVERY_HOURS[props.span] === 0)
    .map((point) => point.at);
  const dayView = TICK_EVERY_HOURS[props.span] === 24;
  const config = {
    value: { label: ov('movement.value'), color: 'var(--primary-ink)' },
  } satisfies ChartConfig;

  return (
    <OverviewCard title={ov('movement.title')}>
      {measured ? (
        <ChartContainer
          config={config}
          role="img"
          aria-label={ov('movement.chart')}
          initialDimension={{ width: 700, height: 176 }}
          className="aspect-auto h-44 w-full rounded-md focus-within:ring-2 focus-within:ring-ring"
        >
          <AreaChart data={data}>
            <XAxis
              dataKey="at"
              type="number"
              domain={['dataMin', 'dataMax']}
              ticks={ticks}
              interval={0}
              padding={{ left: 12, right: 12 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(at: number) =>
                dayView ? dayLabel(new Date(at)) : hourLabel(new Date(at))
              }
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(_label, payload) => {
                    // The label is the hovered x value under the pointer and an index under
                    // keyboard focus, so the instant is read off the point itself.
                    const instant: unknown = payload[0]?.payload?.at;
                    if (typeof instant !== 'number') return null;
                    const at = new Date(instant);
                    return `${dayLabel(at)} ${hourLabel(at)}`;
                  }}
                  formatter={(value) => (typeof value === 'number' ? count(value, locale) : '')}
                />
              }
            />
            <Area
              dataKey="value"
              type="monotone"
              stroke="var(--color-value)"
              fill="var(--color-value)"
              fillOpacity={0.15}
              strokeWidth={2}
              connectNulls={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ChartContainer>
      ) : (
        <NoReading />
      )}
    </OverviewCard>
  );
}
